import { useState, useEffect } from 'react'
import { ReactSVG } from 'react-svg'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'

import {
	updateLegalContent,
	getCompanyRoles,
	getRoles,
	getPermissions,
	getReelMemberById,
} from '../../redux/modules/user/actions'
import Alert from '../common/Alert'
import ConfirmationModalNew from '../common/ConfirmationModalNew'
import NewToolTip from '../common/New-ToolTip'
import { minorAilments as minorAilmentsData, medicationReviewTypes } from '../patient/text/data'

import TermsOfUseContent from './legal/TermsOfUseContent'
import PrivacyPolicyContent from './legal/PrivacyPolicyContent'
import DisclaimerContent from './legal/DisclaimerContent'
import MemberDetails from './profile/MemberDetails'
import ComingSoon from '../ComingSoon'
import EmailPassword from './settings/EmailPassword'
import VirtualSignature from './settings/VirtualSignature'

/**
 * Skeleton loader component displayed while user details are loading
 *
 * @returns {JSX.Element} The skeleton loader UI
 */
const SkeletonLoader = () => {
	return (
		<div className="h-[calc(100vh-theme(spacing.header)-theme(spacing.footer)-25px)] bg-gray-100">
			<div className="w-full h-full">
				<div className="h-full bg-white shadow">
					<div className="flex h-full">
						{/* Left Panel Skeleton */}
						<div className="w-64 h-full border-r border-gray-200">
							<div className="flex items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
								<div className="w-6 h-6 bg-gray-200 rounded-full animate-pulse"></div>
								<div className="w-32 h-6 ml-3 bg-gray-200 rounded animate-pulse"></div>
							</div>
							<nav className="p-4 space-y-1">
								{[1, 2, 3, 4].map((item) => (
									<div
										key={item}
										className="h-10 bg-gray-100 rounded-md animate-pulse"
									></div>
								))}
							</nav>
						</div>

						{/* Right Content Area Skeleton */}
						<div className="flex flex-col flex-1 h-full">
							<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
								<div className="w-48 h-6 bg-gray-200 rounded animate-pulse"></div>
								<div className="flex items-center">
									<div className="w-3 h-3 mr-2 bg-gray-200 rounded-full"></div>
									<div className="w-24 h-4 bg-gray-200 rounded animate-pulse"></div>
								</div>
							</div>
							<div className="flex-1 p-6">
								<div className="h-[500px] flex flex-col items-center mt-36">
									<div className="w-40 h-10 mb-6 bg-gray-200 rounded animate-pulse"></div>
									<div className="h-[500px] w-full max-w-3xl border border-gray-100 rounded-md">
										<div className="p-6 border-b border-gray-200 bg-gray-50">
											<div className="w-48 h-6 mx-auto bg-gray-200 rounded animate-pulse"></div>
										</div>
										<div className="p-6">
											{[1, 2, 3, 4, 5].map((item) => (
												<div
													key={item}
													className="h-4 mb-4 bg-gray-100 rounded animate-pulse"
												></div>
											))}
										</div>
										<div className="p-4 border-t border-gray-200 bg-gray-50">
											<div className="flex items-center justify-between">
												<div className="w-48 h-4 bg-gray-200 rounded animate-pulse"></div>
												<div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

/**
 * MyProfile Component
 *
 * Main profile management component that allows users to view and edit their profile information,
 * manage account settings, and handle legal document acceptance (Terms of Use, Privacy Policy).
 *
 * Features:
 * - Profile information display and editing
 * - Account settings management
 * - Legal document acceptance workflow
 * - Navigation between different profile sections
 *
 * @returns {JSX.Element} The rendered MyProfile component
 */
// Constants for previous location tracking
const PREVIOUS_LOCATION_STORAGE_KEY = 'myProfilePreviousLocation';
const MY_PROFILE_ROUTE = '/my-profile';
const DEFAULT_ROUTE = '/patients';
const FIRST_TIME_LEGAL_FLOW_KEY = 'isFirstTimeLegalFlow';
const TRIGGER_COMPLIANCE_ON_LEGAL_PAGE_KEY = 'triggerComplianceOnLegalPage';
const LEGAL_SNAPSHOT_KEY_PREFIX = 'myProfileLegalSnapshot';
const LEGAL_COMPLETION_POPUP_SHOWN_KEY_PREFIX = 'myProfileLegalCompletionPopupShown';

const MyProfile = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const defaultTab = searchParams.get('selected') || 'profile'
	const [activeTab, setActiveTab] = useState(defaultTab)
	const [declineType, setDeclineType] = useState(null)
	const dispatch = useDispatch()
	const { currentLoggedInUserDetail } = useSelector(
		(state) => state.currentLoggedInUserDetail
	)
	const { userDetailsLoading } = useSelector(
		(state) => state.userDetailsLoading
	)
	const { companyRoles } = useSelector((state) => state.companyRoles || { companyRoles: [] })
	const { roles } = useSelector((state) => state.roles || { roles: [] })
	const { permissions } = useSelector((state) => state.permissions || { permissions: [] })
	const { minorAilments, medicationReviews } = useSelector(
		(state) => state.clinicalService?.clinicalServicesSmall || { minorAilments: [], medicationReviews: [] }
	)
	const [showDeclinePopup, setShowDeclinePopup] = useState(false)
	const [isDeclined, setIsDeclined] = useState(false)
	const navigate = useNavigate()
	const location = useLocation()
	const [showLegalDocsCompleted, setShowLegalDocsCompleted] = useState(false);
	const [initialLegalSnapshot, setInitialLegalSnapshot] = useState(null)
	
	// Store the previous location when component mounts (if not already stored)
	useEffect(() => {
		
		// Priority 1: Use location state if provided (most reliable)
		if (location.state?.from) {
			sessionStorage.setItem(PREVIOUS_LOCATION_STORAGE_KEY, location.state.from);
			return;
		}
		
		// Priority 2: Check if we already have a stored location
		if (sessionStorage.getItem(PREVIOUS_LOCATION_STORAGE_KEY)) {
			return;
		}
		
		// Priority 3: Extract from browser referrer (fallback)
		const referrer = document.referrer;
		if (!referrer?.includes(window.location.origin)) {
			return;
		}
		
		const referrerPath = new URL(referrer).pathname;
		const currentPath = location.pathname;
		
		// Store if it's a valid, different path
		if (referrerPath !== currentPath && 
		    referrerPath !== MY_PROFILE_ROUTE && 
		    referrerPath.startsWith('/')) {
			sessionStorage.setItem(PREVIOUS_LOCATION_STORAGE_KEY, referrerPath);
		}
	}, [location.state, location.pathname])

	const shouldFetchRoleMetadata = !companyRoles?.length || !roles?.length || !permissions?.length

	// Fetch role metadata only when not already in store (protects against re-mount/dispatch loops)
	useEffect(() => {
		if (!shouldFetchRoleMetadata) {
			return
		}
		dispatch(getCompanyRoles())
		dispatch(getRoles())
		dispatch(getPermissions())
	}, [dispatch, shouldFetchRoleMetadata])

	// Fetch reel member data to supplement user details with expiry dates and other fields
	useEffect(() => {
		if (currentLoggedInUserDetail?.userId) {
			dispatch(getReelMemberById(currentLoggedInUserDetail.userId))
		}
	}, [dispatch, currentLoggedInUserDetail?.userId])

	// Update activeTab when URL parameter changes
	useEffect(() => {
		const selectedTab = searchParams.get('selected')
		const tabParam = searchParams.get('tab')
		
		if (selectedTab) {
			// selectedTab is the parent section to open
			setOpenSection(selectedTab)
			
			// tabParam is the child tab to activate (for nested navigation like Settings > Virtual Signature). If there's a tab parameter, use it for activeTab, otherwise use the selectedTab
			if (tabParam) {
				setActiveTab(tabParam)
			} else {
				setActiveTab(selectedTab)
			}
		}
	}, [searchParams])

	// Capture the initial legal state snapshot for this user when page/session starts.
	useEffect(() => {
		if (!currentLoggedInUserDetail?.userId) {
			return
		}

		const userId = currentLoggedInUserDetail.userId
		const snapshotStorageKey = `${LEGAL_SNAPSHOT_KEY_PREFIX}:${userId}`
		const popupShownKey = `${LEGAL_COMPLETION_POPUP_SHOWN_KEY_PREFIX}:${userId}`
		const rawSnapshot = sessionStorage.getItem(snapshotStorageKey)
		const currentSnapshot = {
			acceptedTermOfUse: Boolean(currentLoggedInUserDetail?.acceptedTermOfUse),
			acceptedPrivacyPolicy: Boolean(currentLoggedInUserDetail?.acceptedPrivacyPolicy)
		}
		const isCurrentIncomplete =
			!currentSnapshot.acceptedTermOfUse || !currentSnapshot.acceptedPrivacyPolicy

		if (rawSnapshot) {
			try {
				const parsedSnapshot = JSON.parse(rawSnapshot)
				const isStoredComplete =
					Boolean(parsedSnapshot?.acceptedTermOfUse) &&
					Boolean(parsedSnapshot?.acceptedPrivacyPolicy)

				// If DB/state is reset to incomplete, refresh stale stored snapshot and allow popup again.
				if (isStoredComplete && isCurrentIncomplete) {
					sessionStorage.setItem(snapshotStorageKey, JSON.stringify(currentSnapshot))
					sessionStorage.removeItem(popupShownKey)
					setInitialLegalSnapshot(currentSnapshot)
				} else {
					setInitialLegalSnapshot(parsedSnapshot)
				}
			} catch {
				sessionStorage.setItem(snapshotStorageKey, JSON.stringify(currentSnapshot))
				if (isCurrentIncomplete) {
					sessionStorage.removeItem(popupShownKey)
				}
				setInitialLegalSnapshot(currentSnapshot)
			}
			return
		}

		sessionStorage.setItem(snapshotStorageKey, JSON.stringify(currentSnapshot))
		if (isCurrentIncomplete) {
			sessionStorage.removeItem(popupShownKey)
		}
		setInitialLegalSnapshot(currentSnapshot)
	}, [
		currentLoggedInUserDetail?.userId,
		currentLoggedInUserDetail?.acceptedTermOfUse,
		currentLoggedInUserDetail?.acceptedPrivacyPolicy
	])

	// Show completion popup when initial snapshot was incomplete and current state becomes complete.
	useEffect(() => {
		if (!currentLoggedInUserDetail?.userId || !initialLegalSnapshot) {
			return
		}

		const userId = currentLoggedInUserDetail.userId
		const popupShownKey = `${LEGAL_COMPLETION_POPUP_SHOWN_KEY_PREFIX}:${userId}`
		const wasInitiallyComplete =
			Boolean(initialLegalSnapshot?.acceptedTermOfUse) &&
			Boolean(initialLegalSnapshot?.acceptedPrivacyPolicy)
		const isCurrentlyComplete =
			Boolean(currentLoggedInUserDetail?.acceptedTermOfUse) &&
			Boolean(currentLoggedInUserDetail?.acceptedPrivacyPolicy)

		if (!wasInitiallyComplete && isCurrentlyComplete && sessionStorage.getItem(popupShownKey) !== 'true') {
			setShowLegalDocsCompleted(true)
			sessionStorage.setItem(popupShownKey, 'true')
		}
	}, [
		initialLegalSnapshot,
		currentLoggedInUserDetail?.userId,
		currentLoggedInUserDetail?.acceptedTermOfUse,
		currentLoggedInUserDetail?.acceptedPrivacyPolicy
	])

	// Function to get company role name by ID
	const getCompanyRoleName = (companyRoleId) => {
		const role = companyRoles?.find((r) => r.id === companyRoleId)
		return role?.companyRole || 'Project Manager' // Fallback to default if not found
	}

	// Constants for route mapping
	const ROUTE_MAP = {
		'dashboard': 'Dashboard',
		'patients': 'Patients',
		'clinicals': 'Clinicals',
		'clinical-services': 'Clinical Services',
		'scheduler': 'Scheduler',
		'schedular': 'Scheduler',
		'team': 'Team',
		'support': 'Support',
		'my-profile': 'My Profile',
	};

	// Helper function to get service display name
	const getServiceDisplayName = (serviceId) => {
		const medicationReview = medicationReviews?.find((mr) => String(mr.id) === String(serviceId));
		if (medicationReview) {
			return 'Medication Review';
		}

		const minorAilment = minorAilments?.find((ma) => String(ma.id) === String(serviceId));
		if (minorAilment?.minorAilmentTypeCode) {
			const minorAilmentData = minorAilmentsData?.find(
				(item) => item.code?.toLowerCase() === minorAilment.minorAilmentTypeCode?.toLowerCase()
			);
			return minorAilmentData?.title || null;
		}

		return null;
	};

	// Helper function to parse route
	const parseRoute = (route) => {
		const cleanRoute = route.split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '');
		const pathParts = cleanRoute.split('/').filter(part => part.length > 0);
		return {
			cleanRoute,
			pathParts,
			firstPart: pathParts[0]?.toLowerCase(),
			secondPart: pathParts[1]?.toLowerCase(),
			thirdPart: pathParts[2]?.toLowerCase(),
		};
	};

	// Function to get previous location from various sources
	const getPreviousLocation = (location) => {
		return location.state?.from 
			|| sessionStorage.getItem(PREVIOUS_LOCATION_STORAGE_KEY) 
			|| DEFAULT_ROUTE;
	}

	// Function to get page name from route
	const getPageNameFromRoute = (route) => {
		if (!route || route === DEFAULT_ROUTE) {
			return 'Patients';
		}

		const { cleanRoute, pathParts, firstPart, secondPart, thirdPart } = parseRoute(route);

		// Handle patient routes
		if (firstPart === 'patient' && secondPart) {
			// Clinical service route: /patient/:id/clinical-services/:serviceId
			if (pathParts.length >= 4 && thirdPart === 'clinical-services') {
				return getServiceDisplayName(pathParts[3]) || 'Clinical Services';
			}

			// All patient chart routes (tabs, details, etc.) return just "Patient Chart"
			return 'Patient Chart';
		}

		// Handle clinical services routes
		if (firstPart === 'clinical-services') {
			return 'Clinical Services';
		}

		// Handle mapped routes
		if (ROUTE_MAP[firstPart]) {
			return ROUTE_MAP[firstPart];
		}

		// Fallback: capitalize first letter
		return firstPart ? firstPart.charAt(0).toUpperCase() + firstPart.slice(1) : 'Back';
	}

	// Function to handle back navigation
	const handleBackNavigation = () => {
		if (currentLoggedInUserDetail?.acceptedTermOfUse && currentLoggedInUserDetail?.acceptedPrivacyPolicy) {
			const previousLocation = getPreviousLocation(location);
			sessionStorage.removeItem(PREVIOUS_LOCATION_STORAGE_KEY);
			navigate(previousLocation);
		}
	}

	const tooltip = (tooltipId, place) => {
		return (
			<NewToolTip
				id={tooltipId}
				place={place || 'right'}
				offset= {-120}
				render={() => (
					<div className="flex flex-col max-w-md p-2 bg-gray-20">
						<p className="font-normal text-left text-teal-300 text-body-sm">
							Coming Soon
						</p>
					</div>
				)}
			/>
		)
	}

	// Track which navigation section is currently open
	const [openSection, setOpenSection] = useState(defaultTab)

	// Navigation sections configuration with icons and content
	const activeStyle = 'bg-teal-25 border-l-4 pl-14 border-teal-100 text-REELTeal mb-2 text-[18px]'
	const inactiveStyle = 'text-teal-300 pl-[61px] mb-2 text-[18px] '
	const navSections = [
		{
			key: 'profile',
			label: <span className="text-xl">My Profile</span>,
			icon: (
				<ReactSVG
					src="/icons/profile-new.svg"
					beforeInjection={(svg) => {
						svg.setAttribute('class', 'w-7 h-7')
					}}
					className="mr-4"
				/>
			),
			content: (
				<div className="flex flex-col">
					<button
						onClick={() => setActiveTab('profile')}
						className={`w-full text-start py-4 font-medium 
            			${activeTab === 'profile' ? activeStyle : inactiveStyle}`}
					>
						Member Details
					</button>
					<button
						// onClick={() => setActiveTab('photo')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'photo' ? activeStyle : inactiveStyle}`}
						data-tooltip-id="photo-tooltip"
					>
						<span className="inline-flex items-center gap-1">
							<span
								className="inline-block align-middle"
								tabIndex={0}
							>
								Photo
							</span>
						</span>
					</button>
					{tooltip('photo-tooltip')}

					<button
						// onClick={() => setActiveTab('biography')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'biography' ? activeStyle : inactiveStyle}`}
						data-tooltip-id="biography-tooltip"
					>
						<span className="inline-flex items-center gap-1">
							<span
								className="inline-block align-middle"
								tabIndex={0}
							>
								Biography
							</span>
						</span>
					</button>
					{tooltip('biography-tooltip')}
					{/* tooltips consolidated to single shared instance below */}
				</div>
			)
		},
		{
			key: 'settings',
			label: <span className="text-xl">Settings</span>,
			icon: (
				<ReactSVG
					src="/icons/settings-new.svg"
					beforeInjection={(svg) => {
						svg.setAttribute('class', 'w-7 h-7')
					}}
					className="mr-4"
				/>
			),
			content: (
				<div className="flex flex-col">
					<button
						onClick={() => setActiveTab('settings')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'settings' ? activeStyle : inactiveStyle}`}
					>
						Username & Password
					</button>
					<button
						// onClick={() => setActiveTab('multi-factor')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'multi-factor' ? activeStyle : inactiveStyle}`}
						data-tooltip-id="multi-factor-tooltip"
					>
						<span className="inline-flex items-center gap-1">
							<span
								className="inline-block align-middle"
								tabIndex={0}
							>
								Multi-Factor Authentication
							</span>
						</span>
					</button>
					{tooltip('multi-factor-tooltip')}

					<button
						onClick={() => setActiveTab('virtual-signature')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'virtual-signature' ? activeStyle : inactiveStyle}`}
					>
						Virtual Signature
					</button>

					<button
						// onClick={() => setActiveTab('notification')}
						className={`w-full text-start py-4 font-medium 
		    			${activeTab === 'notification' ? activeStyle : inactiveStyle}`}
						data-tooltip-id="notification-tooltip"
					>
						<span className="inline-flex items-center gap-1">
							<span
								className="inline-block align-middle"
								tabIndex={0}
							>
								Notification
							</span>
						</span>
					</button>
					{tooltip('notification-tooltip')}
				</div>
			)
		},
		{
			key: 'clinical-schedule',
			label: <span className="text-xl">Clinical Schedule</span>,
			icon: (
				<ReactSVG
					src="/icons/clinical-schedule-new.svg"
					beforeInjection={(svg) => {
						svg.setAttribute('class', 'w-7 h-7')
					}}
					className="mr-4"
				/>
			)
		},
		{
			key: 'about',
			label: <span className="text-xl">About REEL</span>,
			icon: (
				<ReactSVG
					src="/icons/about-reel-new.svg"
					beforeInjection={(svg) => {
						svg.setAttribute('class', 'w-7 h-7')
					}}
					className="mr-4"
				/>
			)
		},
		{
			key: 'legal',
			label: <span className="text-xl">Legal</span>,
			icon: (
				<ReactSVG
					src="/icons/legal-new.svg"
					beforeInjection={(svg) => {
						svg.setAttribute('class', 'w-7 h-7')
					}}
					className="mr-4"
				/>
			),
			content: (
				<div className="flex flex-col">
					<button
						onClick={() => setActiveTab('legal')}
						className={`w-full text-start py-4 font-medium
            			${activeTab === 'legal' ? activeStyle : inactiveStyle}`}
					>
						Terms of Use
					</button>
					<button
						onClick={() => setActiveTab('privacy')}
						className={`w-full text-start py-4 font-medium 
            			${activeTab === 'privacy' ? activeStyle : inactiveStyle}`}
					>
						Privacy Policy
					</button>
					<button
						onClick={() => setActiveTab('disclaimer')}
						className={`w-full text-start py-4 font-medium
            			${activeTab === 'disclaimer' ? activeStyle : inactiveStyle}`}
					>
						Disclaimer
					</button>
					<button
						// onClick={() => setActiveTab('security')}
						className={`w-full text-start py-4 font-medium
		    			${activeTab === 'security' ? activeStyle : inactiveStyle}`}
						data-tooltip-id="security-tooltip"
					>
						<span className="inline-flex items-center gap-1">
							<span
								className="inline-block align-middle"
								tabIndex={0}
							>
								Security
							</span>
						</span>
					</button>
					{tooltip('security-tooltip')}
				</div>
			)
		}
	]

	/**
	 * Handles user agreement to legal documents (Terms of Use or Privacy Policy)
	 *
	 * @param {string} type - The type of legal document ('terms' or 'privacy')
	 * @returns {Promise<void>}
	 */
	const handleAgree = async (type) => {
		if (!currentLoggedInUserDetail?.userId) return
		if (type === 'terms') {
			await dispatch(
				updateLegalContent(
					currentLoggedInUserDetail.userId,
					true, // isTermAccepted
					Boolean(currentLoggedInUserDetail?.acceptedPrivacyPolicy)
				)
			)
			
			// Wait for 5 seconds before changing page
			await new Promise(resolve => setTimeout(resolve, 5000));
			
			if (!currentLoggedInUserDetail?.acceptedPrivacyPolicy) {
				setActiveTab('privacy')
			}
		} else if (type === 'privacy') {
			await dispatch(
				updateLegalContent(
					currentLoggedInUserDetail.userId,
					Boolean(currentLoggedInUserDetail?.acceptedTermOfUse),
					true // isPrivacyAccepted
				)
			)
			
			// Wait for 5 seconds before changing page
			await new Promise(resolve => setTimeout(resolve, 5000));
			
			if (!currentLoggedInUserDetail?.acceptedTermOfUse) {
				setActiveTab('legal')
			}
		}
	}

	/**
	 * Handles navigation section clicks and updates URL parameters
	 *
	 * @param {string} sectionKey - The key of the section to navigate to
	 */
	const handleSectionClick = (sectionKey) => {
		const newSection = openSection === sectionKey ? null : sectionKey
		setOpenSection(newSection)
		if (newSection) {
			setSearchParams({ selected: newSection })
		} else {
			setSearchParams({})
		}
	}

	/**
	 * Shows the decline popup when user declines legal documents
	 *
	 * @param {string} type - The type of legal document being declined
	 */
	const handleDecline = (type) => {
		setShowDeclinePopup(true)
		setDeclineType(type)
	}

	/**
	 * Handles close/confirm for Legal Documentation Completed modal.
	 * For first-time legal flow, this primes App-level compliance popup on the legal page.
	 */
	const handleLegalDocsCompletedClose = () => {
		setShowLegalDocsCompleted(false);
		if (sessionStorage.getItem(FIRST_TIME_LEGAL_FLOW_KEY) === 'true') {
			sessionStorage.setItem(TRIGGER_COMPLIANCE_ON_LEGAL_PAGE_KEY, 'true');
			sessionStorage.removeItem(FIRST_TIME_LEGAL_FLOW_KEY);
			// Force a same-route location update so App-level compliance effect runs immediately.
			navigate(`${location.pathname}${location.search}`, {
				replace: true,
				state: {
					...(location.state || {}),
					compliancePopupTriggerAt: Date.now()
				}
			});
		}
	}

	/**
	 * Renders the appropriate content based on the selected tab
	 *
	 * @param {string} tab - The active tab to render content for
	 * @returns {JSX.Element} The rendered tab content
	 */
	function renderTabContent(tab) {
		switch (tab) {
			case 'profile':
				if (currentLoggedInUserDetail) {
					return <MemberDetails user={currentLoggedInUserDetail} />
				} else return <div>Loading profile...</div>
			case 'settings':
				if (currentLoggedInUserDetail) {
					return <EmailPassword user={currentLoggedInUserDetail} />
				} else return <div>Loading settings...</div>
			case 'legal':
				return (
					// Footers are hidden on height less than 900px, work with tailwind config to fix
					<TermsOfUseContent
						handleAgree={handleAgree}
						handleDecline={handleDecline}
						effectiveDate="April 29, 2026"
					/>
				)
			case 'privacy':
				return (
					<PrivacyPolicyContent
						handleAgree={handleAgree}
						handleDecline={handleDecline}
						effectiveDate="April 29, 2026"
					/>
				)
			case 'disclaimer':
				return <DisclaimerContent effectiveDate="April 29, 2026" />
			case 'virtual-signature':
				return <VirtualSignature />
			default:
				return <ComingSoon />
		}
	}

	return (
		<>
			{userDetailsLoading && <SkeletonLoader />}
			{!userDetailsLoading && (
				<>
					{/* Decline popup modal */}
					<ConfirmationModalNew
						open={showDeclinePopup}
						type="primary"
						title={`Declined ${declineType === 'terms'
								? 'Terms of Use'
								: 'Privacy Policy'
							}`}
						content={() => (
							<>
								<b>
									To use this application, please review and accept the <br /><span className="text-teal-900 font-semibold">Terms of Use</span> and <span className="text-teal-900 font-semibold">Privacy Policy</span>.
								</b>
								<br />
								<br />
								<p>
									You can still view your profile; other features will remain unavailable.
								</p>
							</>
						)}
						hideCloseIcon={true}
						cancelText="Back to Legal"
						handleClose={() => {
							setShowDeclinePopup(false)
							setIsDeclined(true)
						}}
						confirmText={
							declineType === 'terms'
								? 'Review Terms of Use'
								: 'Review Privacy Policy'
						}
						// Get behaviour from Vesna
						confirmAction={() => setShowDeclinePopup(false)}
					/>

					{!(
						currentLoggedInUserDetail?.acceptedPrivacyPolicy &&
						currentLoggedInUserDetail?.acceptedTermOfUse
					) &&
						isDeclined && (
							<Alert
								title="Alert"
								type="danger"
								className="m-4 border border-red-100 bg-red-50"
								content={() => (
									<div className="flex items-center text-gray-900">
										<span>
											Must review and accept our{' '}
											<button
												onClick={() => {
													setActiveTab('legal')
													setOpenSection('legal')
													setSearchParams({
														selected: 'legal'
													})
												}}
												className="font-medium text-teal-600  hover:text-teal-700"
											>
												"Terms of Use"
											</button>{' '}
											and{' '}
											<button
												onClick={() => {
													setActiveTab('privacy')
													setOpenSection('legal')
													setSearchParams({
														selected: 'legal'
													})
												}}
												className="font-medium text-teal-600  hover:text-teal-700"
											>
												"Privacy Policy"
											</button>{' '}
											to continue using the application.
											<span
												className="font-semibold text-teal-300 underline cursor-pointer ml-6"
												onClick={() => {
													setActiveTab('legal')
													setOpenSection('legal')
													setSearchParams({
														selected: 'legal'
													})
												}}
											>
												Click to View
											</span>
										</span>
									</div>
								)}
							/>
						)}

					<ConfirmationModalNew
						open={showLegalDocsCompleted}
						type="primary"
						title="Legal Documentation Completed"
						hideCloseIcon={false}
						onCross={handleLegalDocsCompletedClose}
						handleClose={handleLegalDocsCompletedClose}
						confirmText="Okay" // 🔹 Changed from "Continue" to "Okay"
						verticalButtons={false}
						className="!w-[700px] !max-w-[700px]"
						content={() => (
							<div className="px-2 pt-2 pb-0">
								{/* Main message */}
								<div className="mb-4 text-teal-300">
									You have successfully completed the <span className="font-semibold">Terms of Use</span> and <span className="font-semibold">Privacy Policy</span>.
								</div>

								{/* Next-step guidance */}
								<div className="mb-4 text-sm text-gray-600">
									Before you begin, <span className="font-semibold">add your Virtual Signature in Settings</span>.
								</div>
								<div className="mb-4 text-gray-600">
									This signature will automatically be applied to all mandatory documentation in Clinical Services.
								</div>

								{/* Subheading */}
								<div className="mb-2 text-sm font-semibold text-teal-300 border-t border-gray-100 pt-3">
									How to Navigate to Clinical Services:
								</div>

								{/* Bullet points */}
								<ul className="mb-6 space-y-2 text-gray-800 list-disc list-inside pl-4">
									<li>
										Select the <span className="font-bold">Patients</span> tab.
									</li>
									<li>
										Choose a patient, then click on <span className="font-bold">Clinical Services</span> to start a new service.
									</li>
								</ul>
							</div>
						)}
						confirmAction={handleLegalDocsCompletedClose}
					/>

					<div className="h-[86vh] bg-gray-100 mt-4">
						<div className="w-full h-full">
							<div className="h-full bg-white shadow border-b border-gray-100">
								<div className="flex h-full gap-4 px-4">
									{/* Left Panel */}
									<div className="w-[350px] flex-shrink-0 h-full flex flex-col relative overflow-hidden bg-white">
										{/* Back Button */}
										{(() => {
											const previousLocation = getPreviousLocation(location);
											const previousPageName = getPageNameFromRoute(previousLocation);
											
											return (
												<div className="flex items-center pl-2 sm:pl-4 pr-4 sm:pr-6 pt-1 pb-3 bg-white border-b border-gray-100">
													<button
														onClick={handleBackNavigation}
														className="flex items-center gap-2 text-gray-700 hover:text-teal-600 transition-colors group"
													>
														<div className="w-7 h-7 rounded-full bg-gray-20 flex items-center justify-center">
															<ChevronLeftIcon className="w-5 h-5" />
														</div>
														<span className="text-md md:text-base font-semibold group-hover:underline underline-offset-2"> Back to {previousPageName}</span>
													</button>
												</div>
											);
										})()}

										{/* Left Header */}
										<div className="flex items-center px-6 py-4 bg-gray-20 max-h-16 flex-shrink-0 z-10 gap-3">
											<ReactSVG
												src="/icons/dark-pill.svg"
												beforeInjection={(svg) => svg.setAttribute('class', 'w-10 h-10')}
											/>
											<h1 className="text-[25px] font-bold text-teal-300 ">
												Clinicals
											</h1>
										</div>

										{/* Navigation */}
										<nav className="flex-1 p-4 space-y-1 min-h-0 border-r-[3px] border-gray-100 overflow-y-auto">
											{navSections.map((section) => {
												const hasContent =
													!!section.content

												return (
													<div
														key={section.key}
													>
														<button
															className={`flex items-center w-full p-4 text-left 
															${hasContent ? 'hover:bg-gray-20' : ''}`}
															onClick={() =>
																hasContent &&
																handleSectionClick(section.key)
															}
															aria-disabled={!hasContent}
															data-tooltip-id={!hasContent ? `${section.key}-main-tooltip` : undefined}
														>
															{section.icon}
															<span className={`flex-1 ${openSection === section.key ? 'font-semibold' : 'font-medium'} text-[18px] text-teal-300`}>
																<span>
																	{section.label}
																</span>
															</span>

															{/* Only show arrow if content exists */}
															{hasContent && (
																<svg
																	className={`w-6 h-6 transform transition-transform duration-200 text-teal-300
																		${openSection === section.key ? 'rotate-180' : ''}`}
																	fill="none"
																	stroke="currentColor"
																	viewBox="0 0 24 24"
																>
																	<path
																		strokeLinecap="round"
																		strokeLinejoin="round"
																		strokeWidth="2"
																		d="M19 9l-7 7-7-7"
																	/>
																</svg>
															)}
														</button>

														{hasContent &&
															openSection === section.key && (
																<div className="bg-white">
																	{section.content}
																</div>
															)}
													</div>
												)
											})}
										</nav>

										{/* Individual tooltips for main navigation sections without content */}
										{tooltip('clinical-schedule-main-tooltip')}
										{tooltip('about-main-tooltip')}

										 {/* Save & Close Button */}
										 <div className="mt-auto mb-5 p-4 bg-white border-r-[3px] border-gray-100 border-b border-gray-100">
											<button
												className="w-full h-[60px] px-8 py-3.5 font-semibold text-white transition-colors rounded shadow bg-teal-150 hover:bg-teal-200"
												onClick={handleBackNavigation}
											>
												Exit My Profile
											</button>
										</div>
									</div>

									{/* Right Content Area */}
									<div className="flex flex-col flex-1 h-full min-w-0 w-full">
										{/* Right Header - UPDATED TO USE DYNAMIC COMPANY ROLE */}
										<div className="flex-shrink-0 w-full bg-white border-b border-gray-100 h-[44px]" />
										<div className="flex items-center justify-between flex-shrink-0 w-full px-6 py-4 bg-gray-20">
											<div className="text-xl font-semibold text-teal-300">
												{currentLoggedInUserDetail?.firstName +
													' ' + currentLoggedInUserDetail?.lastName ||
													'Example User'}{' '}-{' '}
												{getCompanyRoleName(currentLoggedInUserDetail?.companyRoleId)}
											</div>
											<span className={`flex items-center border rounded text-[16px] px-3 py-1 mr-4
												${currentLoggedInUserDetail?.status === '0'
													? 'bg-green-50 border-green-200'
													: 'bg-red-50 border-red-200'}`}
											>
												<span className={`h-2 w-2 rounded-full mr-2
													${currentLoggedInUserDetail?.status === '0'
														? 'bg-green-600'
														: 'bg-red-600'}`}
												/>
												{currentLoggedInUserDetail?.status === '0'
													? 'Active Member'
													: 'InActive Member'}
											</span>
										</div>

										{/* Content Area */}
										<div className="flex flex-col flex-1 min-h-0 w-full overflow-x-auto overflow-y-hidden">
											{renderTabContent(activeTab)}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</>
			)}
		</>
	)
}

export default MyProfile
